import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useRef, useEffect, useState, createElement } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PAGE_META } from '../utils/pageTitles';
import { BrainCircuit, Code2, Trophy, ArrowDown, Video, Star, Users, Activity, Share2, UsersRound, Lightbulb, Rocket, Award, Crown, Target, Brain, Code, ArrowRight, Clock } from 'lucide-react';
import InterviewSimulator from '../components/InterviewSimulator';
import { SEO } from '../components/SEO';
import { VisitorCounter } from '../components/VisitorCounter';
import { VisitorForm } from '../components/VisitorForm';
import { visitorDataService } from '../services/visitorDataService';
import { visitorService } from '../services/visitorService';
import GeeksLeague from '../components/GeeksLeague';

const moroccanNames = [
  "Youssef",
  "Mohammed",
  "Ahmed",
  "Amine",
  "Hamza",
  "Omar",
  "Mehdi",
  "Karim",
  "Soufiane",
  "Zakaria",
  "Reda",
  "Nabil",
  "Rachid",
  "Bilal",
  "Othmane",
  "Saad",
  "Ismail",
  "Ayoub",
  "Achraf",
  "Badr"
];

const challenges = [
  "Advanced LINQ Operations",
  "Async/Await Patterns",
  "Design Patterns in C#",
  "Entity Framework Core",
  "API Authentication",
  "Dependency Injection",
  "Microservices Architecture",
  "Unit Testing with xUnit",
  "SignalR Real-time Apps",
  "Blazor Components",
  "GraphQL with Hot Chocolate",
  "Azure Functions",
  "gRPC Services",
  "Memory Management",
  "Performance Optimization",
  "Security Best Practices",
  "Clean Architecture",
  "Domain-Driven Design",
  "CQRS Implementation",
  "Event Sourcing"
];

const timeUnits = ['seconds', 'minutes', 'hours'];

const getRandomTime = () => {
  const unit = timeUnits[Math.floor(Math.random() * timeUnits.length)];
  const value = Math.floor(Math.random() * (unit === 'hours' ? 23 : 59)) + 1;
  return `${value} ${unit} ago`;
};

const getRandomItem = (array: string[]) => {
  return array[Math.floor(Math.random() * array.length)];
};

const generateActivityItems = (count: number) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    name: getRandomItem(moroccanNames),
    challenge: getRandomItem(challenges),
    time: getRandomTime(),
    avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(getRandomItem(moroccanNames))}&backgroundColor=512BD4`
  }));
};

function StatisticCard({ icon: Icon, value, label }: {
  icon: any;
  value: string;
  label: string;
}) {
  return (
    <motion.div
      className="bg-white/5 backdrop-blur-sm rounded-lg p-6 text-center"
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Icon className="w-8 h-8 text-[#512BD4] mx-auto mb-3" />
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-gray-400 text-sm">{label}</div>
    </motion.div>
  );
}

function GeeksLeagueSection() {
  return (
    <section className="relative py-24 bg-gradient-to-b from-[#190B4D] to-black overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
      </div>

      <div className="relative container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* League Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block">
              <h2 className="text-6xl md:text-7xl font-bold text-white mb-6 relative">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#512BD4] to-[#7B61FF]">
                  GeeksLeague
                </span>
                <div className="absolute -top-6 -right-6 text-yellow-500 animate-bounce">
                  <Crown className="w-8 h-8" />
                </div>
              </h2>
              <div className="text-xl text-gray-400 italic">Where Code Meets Competition</div>
            </div>
          </motion.div>

          {/* League Format */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xl text-gray-300 mb-8">
              Join the ultimate battle arena where developers compete in live problem-solving challenges.
              Three legendary teams, two hours of intense coding, one champion team.
            </p>
          </motion.div>

          {/* Team Leaders */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              {
                name: "Khaireddine",
                title: "Team Alpha Leader",
                image: "/path-to-khaireddine-image.jpg",
                color: "from-red-500 to-orange-500"
              },
              {
                name: "Abdelaziz",
                title: "Team Beta Leader",
                image: "/path-to-abdelaziz-image.jpg",
                color: "from-blue-500 to-purple-500"
              },
              {
                name: "Mohammed",
                title: "Team Gamma Leader",
                image: "/path-to-mohammed-image.jpg",
                color: "from-green-500 to-teal-500"
              }
            ].map((leader, index) => (
              <motion.div
                key={leader.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="text-center"
              >
                <div className={`w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-r ${leader.color} p-1`}>
                  <div className="w-full h-full rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">{leader.name[0]}</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{leader.name}</h3>
                <p className="text-gray-400">{leader.title}</p>
              </motion.div>
            ))}
          </div>

          {/* League Rules Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/5 rounded-lg p-8 mb-12 backdrop-blur-sm"
          >
            <h3 className="text-2xl font-bold text-white mb-6 text-center">League Format</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <Users className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
                <p className="text-gray-300">Three teams led by industry veterans compete in real-time challenges</p>
              </div>
              <div className="flex items-start space-x-3">
                <Clock className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" />
                <p className="text-gray-300">2-hour live sessions on Google Meet with interactive audience</p>
              </div>
              <div className="flex items-start space-x-3">
                <Trophy className="w-6 h-6 text-yellow-400 mt-1 flex-shrink-0" />
                <p className="text-gray-300">Points-based scoring system with seasonal championships</p>
              </div>
              <div className="flex items-start space-x-3">
                <Brain className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                <p className="text-gray-300">Real-world problem solving with .NET technologies</p>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <div className="text-center">
            <Link 
              to="/geeks-league"
              className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-[#512BD4] rounded-lg hover:bg-[#4119B0] transition-all duration-300 hover:scale-105 group"
            >
              <span>Join a Team</span>
              <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
            </Link>
            <p className="mt-4 text-gray-400">Choose your team and compete in the next season</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const breadcrumbs = [
    {
      name: "Home",
      url: "https://lyfy.ltd"
    }
  ];

  const [statistics] = useState({
    challenges: '500+',
    users: '10K+',
    satisfaction: '98%'
  });

  const [activityItems] = useState(() => generateActivityItems(6));
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const checkExistingVisitor = async () => {
      const storedEmail = localStorage.getItem('visitorEmail');
      if (storedEmail) {
        const exists = await visitorService.checkVisitor(storedEmail);
        if (exists) {
          // Update visit count for existing visitor
          const storedName = localStorage.getItem('visitorName');
          if (storedName) {
            await visitorService.saveVisitorData(storedName, storedEmail);
          }
        } else {
          setShowForm(true);
        }
      } else {
        setShowForm(true);
      }
    };

    checkExistingVisitor();
  }, []);

  return (
    <div>
      <Helmet>
        <title>{PAGE_META.home.title}</title>
        <meta name="description" content={PAGE_META.home.description} />
      </Helmet>
      <SEO 
        title="Master .NET Development"
        description="Elevate your C# skills with interactive challenges, quizzes, and AI-powered learning. Join thousands of developers improving their .NET skills."
        keywords="C#, .NET, coding challenges, programming quiz, developer learning, software development"
        ogImage="/images/home-og.jpg"
      />
      <AnimatePresence>
        {showForm && (
          <VisitorForm onSubmit={() => setShowForm(false)} />
        )}
      </AnimatePresence>
      <div ref={ref} className="relative">
        {/* Hero Section with Parallax */}
        <motion.div 
          className="relative min-h-screen flex items-center justify-center overflow-hidden"
          style={{ y }}
        >
          {/* Background Elements */}
          <div className="absolute inset-0 z-0">
            {/* Main gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#512BD4] via-[#190B4D] to-[#000000]" />
            
            {/* Animated grid pattern */}
            <motion.div
              className="absolute inset-0"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundSize: '60px 60px',
              }}
            />

            {/* Floating .NET elements */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute"
                  initial={{
                    x: Math.random() * window.innerWidth,
                    y: Math.random() * window.innerHeight,
                    scale: Math.random() * 0.5 + 0.5,
                    opacity: Math.random() * 0.3 + 0.1
                  }}
                  animate={{
                    y: [null, Math.random() * 100 - 50],
                    rotate: [0, Math.random() * 360]
                  }}
                  transition={{
                    duration: Math.random() * 10 + 10,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "linear"
                  }}
                >
                  <div className="text-white/10 font-mono text-xl">{'{}'}</div>
                </motion.div>
              ))}
            </div>

            {/* Purple glow effect */}
            <div className="absolute inset-0 bg-gradient-to-t from-purple-900/20 via-transparent to-transparent" />
          </div>

          {/* Hero Content */}
          <motion.div 
            className="relative z-10 text-center px-4 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="mb-8"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/7/7d/Microsoft_.NET_logo.svg"
                alt=".NET Logo"
                className="h-24 mx-auto mb-6"
              />
            </motion.div>

            <motion.h1 
              className="text-4xl md:text-7xl font-bold text-white mb-6 font-mono"
              style={{ opacity }}
            >
              Master <span className="text-[#512BD4]">.NET</span> Development
            </motion.h1>
            <motion.p 
              className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto"
              style={{ opacity }}
            >
              Elevate your C# skills with interactive challenges, quizzes, and AI-powered learning.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row justify-center gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <button
                onClick={() => navigate('/quiz')}
                className="group relative px-8 py-4 rounded-lg font-medium bg-[#512BD4] text-white hover:bg-[#6B42E7] transition-all duration-300 transform hover:-translate-y-1"
              >
                <span className="relative z-10 flex items-center justify-center">
                  <BrainCircuit className="w-5 h-5 mr-2" />
                  Start Quiz
                </span>
                <div className="absolute inset-0 bg-white rounded-lg opacity-0 group-hover:opacity-10 transition-opacity" />
              </button>
              <button
                onClick={() => navigate('/coding-challenge')}
                className="group relative px-8 py-4 rounded-lg font-medium bg-[#190B4D] text-white hover:bg-[#231267] transition-all duration-300 transform hover:-translate-y-1"
              >
                <span className="relative z-10 flex items-center justify-center">
                  <Code2 className="w-5 h-5 mr-2" />
                  Try Challenges
                </span>
                <div className="absolute inset-0 bg-white rounded-lg opacity-0 group-hover:opacity-10 transition-opacity" />
              </button>
            </motion.div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div 
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ArrowDown className="w-6 h-6 text-white/50" />
          </motion.div>
        </motion.div>

        {/* Add GeeksLeague Section */}
        <GeeksLeague />

        {/* Add Statistics Section before Features */}
        <div className="relative bg-[#190B4D] py-16">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <StatisticCard
                icon={Code2}
                value={statistics.challenges}
                label="Coding Challenges"
              />
              <StatisticCard
                icon={Users}
                value={statistics.users}
                label="Active Users"
              />
              <StatisticCard
                icon={Star}
                value={statistics.satisfaction}
                label="User Satisfaction"
              />
            </motion.div>
          </div>
        </div>

        {/* Add Latest Activity Feed */}
        <div className="relative bg-[#231267] py-16">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              className="mb-12 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-white mb-4">Latest Activity</h2>
              <p className="text-gray-400">See what other developers are working on</p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {activityItems.map((item) => (
                <motion.div
                  key={item.id}
                  className="bg-white/5 backdrop-blur-sm rounded-lg p-4"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-blue-500"
                    />
                    <div>
                      <div className="text-white font-medium">{item.name}</div>
                      <div className="text-gray-400 text-sm">{item.time}</div>
                    </div>
                  </div>
                  <div className="text-gray-300">
                    Completed challenge: "{item.challenge}"
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Features Section */}
        <div className="relative bg-[#190B4D] py-24 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <FeatureCard 
                icon={<BrainCircuit className="w-12 h-12 text-[#512BD4]" />}
                title="Interactive Quizzes"
                description="Test your knowledge with our comprehensive quiz system covering various .NET topics."
                image="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2070&auto=format&fit=crop"
              />
              <FeatureCard 
                icon={<Code2 className="w-12 h-12 text-[#512BD4]" />}
                title="Coding Challenges"
                description="Practice real-world coding problems with our interactive C# editor."
                image="https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=2070&auto=format&fit=crop"
              />
              <FeatureCard 
                icon={<Trophy className="w-12 h-12 text-[#512BD4]" />}
                title="Track Progress"
                description="Monitor your learning journey and see your improvement over time."
                image="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015&auto=format&fit=crop"
              />
            </motion.div>

            {/* Interview Practice Section */}
            <motion.div
              className="mt-16 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-white mb-4">Practice Technical Interviews</h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Prepare for your next .NET interview with our AI-powered interview simulator. 
                Get real-time feedback and improve your interview skills.
              </p>
              <motion.div
                className="inline-block"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <button
                  onClick={() => navigate('/interviews')}
                  className="group relative px-8 py-4 rounded-lg font-medium bg-gradient-to-r from-[#512BD4] to-[#6B42E7] text-white hover:from-[#6B42E7] hover:to-[#512BD4] transition-all duration-300"
                >
                  <span className="flex items-center">
                    <Video className="w-5 h-5 mr-2" />
                    Start Interview Practice
                  </span>
                </button>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Contributor Section */}
        <div className="relative bg-[#190B4D] py-24 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Become a Contributor
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Join our community of .NET experts and help shape the future of learning
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <motion.div
                className="bg-white/5 backdrop-blur-sm rounded-xl p-8 text-center"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="w-20 h-20 mx-auto mb-6 bg-purple-500/10 rounded-full flex items-center justify-center">
                  <Share2 className="w-10 h-10 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Share Knowledge</h3>
                <p className="text-gray-300">
                  Share your expertise and help others learn .NET development
                </p>
              </motion.div>

              <motion.div
                className="bg-white/5 backdrop-blur-sm rounded-xl p-8 text-center"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="w-20 h-20 mx-auto mb-6 bg-blue-500/10 rounded-full flex items-center justify-center">
                  <UsersRound className="w-10 h-10 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Grow Together</h3>
                <p className="text-gray-300">
                  Be part of a community that values learning and growth
                </p>
              </motion.div>

              <motion.div
                className="bg-white/5 backdrop-blur-sm rounded-xl p-8 text-center"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="w-20 h-20 mx-auto mb-6 bg-green-500/10 rounded-full flex items-center justify-center">
                  <Lightbulb className="w-10 h-10 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Make Impact</h3>
                <p className="text-gray-300">
                  Help shape the future of .NET education and development
                </p>
              </motion.div>
            </div>

            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Link
                to="/contribute"
                className="inline-flex items-center px-8 py-4 rounded-lg font-medium bg-gradient-to-r from-[#512BD4] to-[#6B42E7] text-white hover:from-[#6B42E7] hover:to-[#512BD4] transition-all duration-300 transform hover:-translate-y-1"
              >
                <Users className="w-5 h-5 mr-2" />
                Join as Contributor
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Add Interview Simulator */}
        <InterviewSimulator />

        {/* Add Visitor Counter */}
        <VisitorCounter />
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description, image }: { 
  icon: React.ReactNode; 
  title: string; 
  description: string;
  image: string;
}) {
  return (
    <motion.div 
      className="relative overflow-hidden rounded-xl bg-[#231267] shadow-xl hover:shadow-2xl transition-all duration-300"
      whileHover={{ y: -5 }}
    >
      <div className="absolute inset-0">
        <img 
          src={image} 
          alt={title}
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#512BD4]/50 to-[#190B4D]/50" />
      </div>
      <div className="relative p-8">
        <div className="flex justify-center mb-4">{icon}</div>
        <h3 className="text-xl font-semibold text-white mb-2 text-center">{title}</h3>
        <p className="text-gray-300 text-center">{description}</p>
      </div>
    </motion.div>
  );
}
